import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import { ConfirmDialog } from 'shared/components/AppComponents';
import DeleteIcon from '@mui/icons-material/Delete';
import { ToolItem } from './ToolGovernance';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ToolGovernanceListProps {
  toolsList: ToolItem[];
  isLoading: boolean;
  onDeleteTool: (id: string) => void;
}

const ToolGovernanceList = ({
  toolsList,
  isLoading,
  onDeleteTool,
}: ToolGovernanceListProps): ReactElement => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState<string>('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const handleCloseDeleteDialog = (): void => {
    setOpenDeleteDialog(false);
  };

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {toolsList.length > 0 ? (
        <TableContainer>
          <Table
            className="app-table"
            sx={{ minWidth: 650 }}
            aria-label="tools table"
          >
            <TableHead>
              <TableRow>
                <TableCell>Tool Name</TableCell>
                <TableCell>Requires Approval</TableCell>
                {orgACLConfig.policies?.includes(
                  'policies.guardrails.toolgovernance.delete'
                ) && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {toolsList.map((tool) => (
                <TableRow key={tool._id}>
                  <TableCell>{tool.name}</TableCell>

                  <TableCell>
                    <Chip
                      label={tool.approvalRequired ? 'Yes' : 'No'}
                      className="mx-2"
                      sx={{
                        background: 'white',
                        color: 'black',
                      }}
                      size="small"
                    />
                  </TableCell>

                  {orgACLConfig.policies?.includes(
                    'policies.guardrails.toolgovernance.delete'
                  ) && (
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedToolId(tool._id);
                          setOpenDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box className="app-plain-card empty-content">
          <Typography variant="body1">
            No tools allowlisted for this server. With default-deny enabled, all
            tool calls will be blocked.
          </Typography>
        </Box>
      )}

      <ConfirmDialog
        title="Remove Tool"
        subTitle="Removing this tool from the allowlist will cause it to be blocked by default-deny."
        showConfirm={openDeleteDialog}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          setOpenDeleteDialog(false);
          onDeleteTool(selectedToolId);
        }}
        onCancel={handleCloseDeleteDialog}
        onCloseDialog={handleCloseDeleteDialog}
      />
    </>
  );
};

export default ToolGovernanceList;
